import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { nameValidation } from 'validators';
import { BottomSheetContentLayout, Column, FunctionalLoader, Grid, GridItem, Menu, TextField, Typography, useOverlayContext } from 'seagull';
import { Checkmark, Download, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateContact } from '~/business-logic/validators';
import { ErrorText } from '~/constants';
import { useEventReporter, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteMemberBottomSheetFlow.module.scss';
const testPrefix = 'invite-member-bottom-sheet';
const defaultRole = UserRole.Member;
export const GetInviteMemberBottomSheetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InviteMemberBottomSheetTID: {
                DownloadPdf: 'cover-page-download-pdf',
                InviteMember: 'cover-page-invite-member',
                InputFirstName: 'invite-member-bottom-sheet-input-first-name',
                InputLastName: 'invite-member-bottom-sheet-input-last-name',
                InputEmail: 'invite-member-bottom-sheet-input-email',
                InputRole: 'invite-member-bottom-sheet-input-role',
                RoleOptOwner: 'invite-member-bottom-sheet-role-opt-owner',
                RoleOptMember: 'invite-member-bottom-sheet-role-opt-member',
                SendInvitation: 'invite-member-bottom-sheet-send-invitation'
            }
        }
    });
const ROLE_OPTIONS = [
    {
        value: UserRole.Owner
    },
    {
        value: UserRole.Member
    }
];
function InviteMemberRoleSelectionPage(param) {
    let { onSelect, disableOwner } = param;
    const { popBottomSheetPage } = useOverlayContext();
    const { t: texts } = useLocaleKeys('clientFlow.inviteMember');
    const roleOptionTexts = {
        [UserRole.Owner]: {
            text: texts.ownerText(),
            subtext: texts.adminSubtext()
        },
        [UserRole.Member]: {
            text: texts.memberText(),
            subtext: texts.memberSubtext()
        }
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: texts.selectRole()
        },
        children: /*#__PURE__*/ _jsx(Menu, {
            classes: {
                root: styles.roleSelectionMenu
            },
            dimensions: {
                width: 'auto',
                paddingTop: 16
            },
            items: ROLE_OPTIONS.map((param)=>{
                let { value } = param;
                var _ref;
                var _roleOptionTexts_value, _roleOptionTexts_value1;
                return {
                    disabled: disableOwner && value === UserRole.Owner,
                    id: value,
                    title: (_ref = (_roleOptionTexts_value = roleOptionTexts[value]) === null || _roleOptionTexts_value === void 0 ? void 0 : _roleOptionTexts_value.text) !== null && _ref !== void 0 ? _ref : value,
                    body: (_roleOptionTexts_value1 = roleOptionTexts[value]) === null || _roleOptionTexts_value1 === void 0 ? void 0 : _roleOptionTexts_value1.subtext,
                    testId: "".concat(testPrefix, "-role-opt-").concat(value),
                    onClick: ()=>{
                        onSelect(value);
                        popBottomSheetPage();
                    }
                };
            })
        })
    });
}
function InviteMemberFormPage(param) {
    let { onSuccess } = param;
    var _roleOptionTexts_selectedRole;
    const { t: texts } = useLocaleKeys('clientFlow.inviteMember');
    const { pushBottomSheetPage, hideBottomSheet, showErrorToast } = useOverlayContext();
    const worksAt = useStore(selectors.user.business);
    const userRole = useStore(selectors.user.role);
    const { setWorksAt, api, logBI } = useStoreActions();
    const { reportFormSubmission, prepareFormObject } = useEventReporter();
    const { control, formState, handleSubmit, setValue, watch } = useForm({
        mode: 'all',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            role: ''
        }
    });
    const [existingMembers, setExistingMembers] = useState();
    const [inviteSuccess, setInviteSuccess] = useState(false);
    const firstNameValidator = useMemo(()=>nameValidation('First name', {
            required: texts.firstNameRequired()
        }), []);
    const lastNameValidator = useMemo(()=>nameValidation('Last name', {
            required: texts.lastNameRequired()
        }), []);
    const roleValidator = useMemo(()=>nameValidation('Role', {
            required: texts.roleRequired()
        }), []);
    const roleOptionTexts = {
        [UserRole.Owner]: texts.ownerText(),
        [UserRole.Member]: texts.memberText()
    };
    const selectedRole = watch('role');
    const roleLabel = selectedRole ? (_roleOptionTexts_selectedRole = roleOptionTexts[selectedRole]) !== null && _roleOptionTexts_selectedRole !== void 0 ? _roleOptionTexts_selectedRole : selectedRole : texts.selectRole();
    useOnMount(()=>{
        async function retrieveMembers() {
            try {
                const { members } = await api.member.getAll();
                setExistingMembers(members);
            } catch (unused) {
                setExistingMembers([]);
            }
        }
        retrieveMembers();
    });
    const emailRules = ()=>({
            validate: {
                email: validateContact.email,
                emailAlreadyUsed: (email)=>(existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every((m)=>m.email() !== email)) || texts.emailAlreadyMemberError()
            }
        });
    const onValidSubmit = async (param)=>{
        let { firstName, lastName, email, role } = param;
        try {
            const invitedRole = role || defaultRole;
            logBI(12201, {
                email,
                role: invitedRole
            });
            await api.member.invite({
                firstName,
                lastName,
                email,
                role: invitedRole
            });
            if (worksAt) {
                setWorksAt(_object_spread_props(_object_spread({}, worksAt), {
                    membersCount: worksAt.membersCount + 1
                }));
            }
            reportFormSubmission('team-invited-member', [
                prepareFormObject('email', email),
                prepareFormObject('firstName', firstName),
                prepareFormObject('lastName', lastName)
            ]);
            onSuccess();
            setInviteSuccess(true);
            setTimeout(()=>hideBottomSheet(), 2000);
        } catch (err) {
            const e = new ErrorResponse(err);
            const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? texts.pendingInvitationError() : e.hasServerError([
                ServerError.UserAlreadyAffiliatedWithBusiness,
                ServerError.UserAlreadyInBusiness
            ]) ? texts.emailAlreadyMemberError() : ErrorText.UnexpectedErrorTryAgain;
            showErrorToast(text);
        }
    };
    const openRoleSelection = ()=>{
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(InviteMemberRoleSelectionPage, {
                onSelect: (role)=>{
                    setValue('role', role, {
                        shouldValidate: true
                    });
                },
                disableOwner: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
            })
        });
    };
    if (!existingMembers) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            show: true,
            name: "fetch-members-loader"
        });
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: texts.inviteTeamMember()
        },
        footer: {
            paddingTop: 8,
            primaryAction: inviteSuccess ? {
                text: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Checkmark, {}),
                        texts.invitationSent()
                    ]
                }),
                disabled: true
            } : {
                text: texts.sendInvitation(),
                disabled: formState.isSubmitting,
                testId: "".concat(testPrefix, "-send-invitation"),
                onClick: handleSubmit(onValidSubmit)
            }
        },
        children: /*#__PURE__*/ _jsxs(Grid, {
            className: styles.formContent,
            children: [
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "firstName",
                        rules: firstNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: texts.firstName(),
                                testId: "".concat(testPrefix, "-input-first-name")
                            }, connectControllerToTextField(renderProps), firstNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "lastName",
                        rules: lastNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: texts.lastName(),
                                testId: "".concat(testPrefix, "-input-last-name")
                            }, connectControllerToTextField(renderProps), lastNameValidator.dataAttr))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "email",
                        rules: emailRules(),
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                collapseHelperText: false,
                                label: texts.email(),
                                testId: "".concat(testPrefix, "-input-email")
                            }, connectControllerToTextField(renderProps)))
                    })
                }),
                /*#__PURE__*/ _jsx(GridItem, {
                    span: 12,
                    children: /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "role",
                        rules: roleValidator,
                        render: (param)=>{
                            let { field, fieldState } = param;
                            var _fieldState_error;
                            return /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        className: styles.roleSelector,
                                        onClick: openRoleSelection,
                                        "data-testid": "".concat(testPrefix, "-input-role"),
                                        "data-error": fieldState.invalid ? 'true' : undefined,
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            skin: field.value ? undefined : 'secondary',
                                            weight: "normal",
                                            children: roleLabel
                                        })
                                    }),
                                    ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message) && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        skin: "error",
                                        className: styles.errorText,
                                        children: fieldState.error.message
                                    })
                                ]
                            });
                        }
                    })
                })
            ]
        })
    });
}
export function InviteMemberActionsMenu(param) {
    let { onDownloadPdf } = param;
    const { pushBottomSheetPage, hideBottomSheet } = useOverlayContext();
    const { t: texts } = useLocaleKeys('clientFlow.inviteMember');
    const items = [
        {
            id: 'download-pdf',
            title: texts.downloadPdf(),
            prefixIcon: /*#__PURE__*/ _jsx(Download, {}),
            testId: 'cover-page-download-pdf',
            onClick: ()=>{
                onDownloadPdf();
                hideBottomSheet();
            }
        },
        {
            id: 'invite-member',
            title: texts.inviteTeamMember(),
            prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
            testId: 'cover-page-invite-member',
            onClick: ()=>{
                pushBottomSheetPage({
                    content: /*#__PURE__*/ _jsx(InviteMemberFormPage, {
                        onSuccess: ()=>{}
                    })
                });
            }
        }
    ];
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: /*#__PURE__*/ _jsx(Column, {
            className: styles.actionsMenu,
            children: /*#__PURE__*/ _jsx(Menu, {
                classes: {
                    root: styles.actionsMenuRoot
                },
                dimensions: {
                    width: 'auto',
                    paddingTop: 16
                },
                items: items
            })
        })
    });
}
